import { useState, useEffect } from 'react';
import { useStore } from '../../store';

/**
 * Admin settings configuration
 */
interface AppSettings {
  // Quotas
  freeUserDailyQuota: number;
  premiumUserDailyQuota: number;
  
  // Pricing
  premiumMonthlyPrice: number; // Prix paiement unique
  premiumSubscriptionPrice: number; // Prix abonnement mensuel
  
  // PayPal
  paypalClientId: string;
  paypalClientSecret: string;
  paypalPlanId: string;
  paypalWebhookId: string;
  
  // Application
  maintenanceMode: boolean;
  allowNewRegistrations: boolean;
  
  // Sécurité
  twoFactorRequired: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  max2FAAttempts: number;
  lockoutDurationMinutes: number;
  emailLinkExpiryHours: number;
  accountDeletionDelayDays: number;
  
  // Contact
  adminEmail: string;
  
  // SMTP
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  smtpFromName: string;
  smtpFrom: string;
  smtpSecure: boolean;
}

/**
 * SettingsPage Component
 *
 * Admin page for configuring application settings.
 * Features:
 * - Quota configuration for free and premium users
 * - Maintenance mode toggle
 * - Registration control
 * - Security settings
 * - SMTP configuration
 *
 * @validates Requirements 14.1
 */
export function SettingsPage() {
  const token = useStore((state) => state.token);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);
  
  const [settings, setSettings] = useState<AppSettings>({
    freeUserDailyQuota: 20,
    premiumUserDailyQuota: -1,
    premiumMonthlyPrice: 4.99,
    premiumSubscriptionPrice: 0.99,
    paypalClientId: '',
    paypalClientSecret: '',
    paypalPlanId: '',
    paypalWebhookId: '',
    maintenanceMode: false,
    allowNewRegistrations: true,
    twoFactorRequired: true,
    sessionTimeout: 480,
    maxLoginAttempts: 5,
    max2FAAttempts: 3,
    lockoutDurationMinutes: 15,
    emailLinkExpiryHours: 24,
    accountDeletionDelayDays: 30,
    adminEmail: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPass: '',
    smtpFromName: 'Spellion',
    smtpFrom: 'noreply@spellion.com',
    smtpSecure: false,
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [smtpTestMessage, setSmtpTestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Charger les paramètres au montage
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
        setSaveMessage(null);
      } else {
        console.error('Erreur API:', data.error);
        setSaveMessage({ type: 'error', text: data.error?.message || 'Erreur lors du chargement des paramètres' });
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      setSaveMessage({ type: 'error', text: 'Erreur lors du chargement des paramètres' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof AppSettings, value: number | boolean | string) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSaveMessage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      const data = await response.json();

      if (data.success) {
        setSettings(data.data);
        setSaveMessage({ type: 'success', text: 'Paramètres enregistrés avec succès' });
        // Force reload public config to update cached values (price, quota, etc.)
        await loadPublicConfig(true);
      } else {
        setSaveMessage({ type: 'error', text: data.error?.message || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
      setSaveMessage({ type: 'error', text: 'Erreur lors de la sauvegarde des paramètres' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setIsTesting(true);
    setSmtpTestMessage(null);

    try {
      // D'abord sauvegarder les paramètres SMTP (sans passer par handleSave pour éviter le conflit d'état)
      const saveResponse = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      const saveData = await saveResponse.json();
      if (!saveData.success) {
        setSmtpTestMessage({ type: 'error', text: `Erreur sauvegarde: ${saveData.error?.message || 'Erreur inconnue'}` });
        setIsTesting(false);
        return;
      }

      // Mettre à jour les settings avec la réponse
      setSettings(saveData.data);

      // Puis tester la connexion
      const response = await fetch('/api/admin/settings/smtp/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setSmtpTestMessage({ type: 'success', text: 'Connexion SMTP réussie ! Les emails peuvent être envoyés.' });
      } else {
        setSmtpTestMessage({ type: 'error', text: `Échec: ${data.error?.message || 'Erreur inconnue'}` });
      }
    } catch (error) {
      console.error('Erreur test SMTP:', error);
      setSmtpTestMessage({ type: 'error', text: 'Erreur lors du test de connexion SMTP' });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-8 h-8 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span className="ml-3 text-slate-600">Chargement des paramètres...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-slate-900">Paramètres</h1>
        <p className="text-slate-500 text-sm mt-1">Configuration de l'application</p>
      </div>

      {/* Save message */}
      {saveMessage && (
        <div
          className={`mb-6 px-4 py-3 rounded-xl ${
            saveMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {saveMessage.type === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span>{saveMessage.text}</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Quotas Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <span>📊</span>
            Quotas de recherche
          </h2>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="freeQuota" className="block text-sm font-medium text-slate-700">
                  Quota utilisateurs gratuits
                </label>
                <p className="text-xs text-slate-500">Nombre de recherches par jour pour les comptes gratuits</p>
              </div>
              <input
                id="freeQuota"
                type="number"
                min="1"
                max="100"
                value={settings.freeUserDailyQuota}
                onChange={(e) => handleChange('freeUserDailyQuota', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="premiumQuota" className="block text-sm font-medium text-slate-700">
                  Quota utilisateurs premium
                </label>
                <p className="text-xs text-slate-500">-1 = illimité</p>
              </div>
              <input
                id="premiumQuota"
                type="number"
                min="-1"
                max="1000"
                value={settings.premiumUserDailyQuota}
                onChange={(e) => handleChange('premiumUserDailyQuota', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="premiumPrice" className="block text-sm font-medium text-slate-700">
                  Prix paiement unique (€)
                </label>
                <p className="text-xs text-slate-500">Prix pour un paiement unique (1 mois)</p>
              </div>
              <input
                id="premiumPrice"
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                value={settings.premiumMonthlyPrice}
                onChange={(e) => handleChange('premiumMonthlyPrice', parseFloat(e.target.value))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="premiumSubscriptionPrice" className="block text-sm font-medium text-slate-700">
                  Prix abonnement mensuel (€)
                </label>
                <p className="text-xs text-slate-500">Prix mensuel pour l'abonnement récurrent (doit correspondre au Plan PayPal)</p>
              </div>
              <input
                id="premiumSubscriptionPrice"
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                value={settings.premiumSubscriptionPrice}
                onChange={(e) => handleChange('premiumSubscriptionPrice', parseFloat(e.target.value))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <label htmlFor="paypalClientId" className="block text-sm font-medium text-slate-700">
                  PayPal Client ID
                </label>
                <p className="text-xs text-slate-500">Client ID de votre application PayPal (sandbox ou production)</p>
              </div>
              <input
                id="paypalClientId"
                type="text"
                value={settings.paypalClientId}
                onChange={(e) => handleChange('paypalClientId', e.target.value)}
                placeholder="AX3OOms8A6bvjnQvzB2Q4HJK..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <label htmlFor="paypalClientSecret" className="block text-sm font-medium text-slate-700">
                  PayPal Client Secret
                </label>
                <p className="text-xs text-slate-500">Secret de votre application PayPal (requis pour annuler les abonnements)</p>
              </div>
              <input
                id="paypalClientSecret"
                type="password"
                value={settings.paypalClientSecret}
                onChange={(e) => handleChange('paypalClientSecret', e.target.value)}
                placeholder={settings.paypalClientSecret === '********' ? '(inchangé)' : 'EJxxxxxxxxxxxxxxxx...'}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <label htmlFor="paypalPlanId" className="block text-sm font-medium text-slate-700">
                  PayPal Plan ID (abonnement récurrent)
                </label>
                <p className="text-xs text-slate-500">ID du plan créé sur PayPal Business pour les abonnements mensuels</p>
              </div>
              <input
                id="paypalPlanId"
                type="text"
                value={settings.paypalPlanId}
                onChange={(e) => handleChange('paypalPlanId', e.target.value)}
                placeholder="P-XXXXXXXXXXXXXXXXXXXXXXXX"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <label htmlFor="paypalWebhookId" className="block text-sm font-medium text-slate-700">
                  PayPal Webhook ID (optionnel)
                </label>
                <p className="text-xs text-slate-500">ID du webhook pour vérifier les notifications PayPal</p>
              </div>
              <input
                id="paypalWebhookId"
                type="text"
                value={settings.paypalWebhookId}
                onChange={(e) => handleChange('paypalWebhookId', e.target.value)}
                placeholder="ex: 9KA715035X473283P"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
              />
            </div>
          </div>
        </div>

        {/* Application Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <span>⚙️</span>
            Application
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Mode maintenance</p>
                <p className="text-xs text-slate-500">Désactive l'accès aux utilisateurs non-admin</p>
              </div>
              <button
                type="button"
                onClick={() => handleChange('maintenanceMode', !settings.maintenanceMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.maintenanceMode ? 'bg-red-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Inscriptions ouvertes</p>
                <p className="text-xs text-slate-500">Autoriser les nouveaux utilisateurs à créer un compte</p>
              </div>
              <button
                type="button"
                onClick={() => handleChange('allowNewRegistrations', !settings.allowNewRegistrations)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.allowNewRegistrations ? 'bg-green-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.allowNewRegistrations ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <span>🔒</span>
            Sécurité
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">2FA obligatoire (admin)</p>
                <p className="text-xs text-slate-500">Exiger l'authentification à deux facteurs pour les administrateurs</p>
              </div>
              <button
                type="button"
                onClick={() => handleChange('twoFactorRequired', !settings.twoFactorRequired)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.twoFactorRequired ? 'bg-green-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.twoFactorRequired ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {/* Warning if 2FA is enabled but SMTP is not configured */}
            {settings.twoFactorRequired && (!settings.smtpHost || !settings.smtpUser || !settings.smtpPass) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <span className="text-amber-500 mt-0.5">⚠️</span>
                <div>
                  <p className="font-medium">SMTP non configuré</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Le 2FA ne sera pas actif tant que le serveur SMTP n'est pas configuré (hôte, utilisateur et mot de passe requis).
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="sessionTimeout" className="block text-sm font-medium text-slate-700">
                  Timeout session admin (minutes)
                </label>
                <p className="text-xs text-slate-500">Durée d'inactivité avant déconnexion automatique</p>
              </div>
              <input
                id="sessionTimeout"
                type="number"
                min="30"
                max="1440"
                step="30"
                value={settings.sessionTimeout}
                onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="maxLoginAttempts" className="block text-sm font-medium text-slate-700">
                  Tentatives de connexion max
                </label>
                <p className="text-xs text-slate-500">Nombre d'essais avant verrouillage du compte</p>
              </div>
              <input
                id="maxLoginAttempts"
                type="number"
                min="3"
                max="10"
                value={settings.maxLoginAttempts}
                onChange={(e) => handleChange('maxLoginAttempts', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="max2FAAttempts" className="block text-sm font-medium text-slate-700">
                  Tentatives de code 2FA max
                </label>
                <p className="text-xs text-slate-500">Nombre d'essais de code avant verrouillage</p>
              </div>
              <input
                id="max2FAAttempts"
                type="number"
                min="3"
                max="10"
                value={settings.max2FAAttempts}
                onChange={(e) => handleChange('max2FAAttempts', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="lockoutDurationMinutes" className="block text-sm font-medium text-slate-700">
                  Durée de verrouillage (minutes)
                </label>
                <p className="text-xs text-slate-500">Temps avant de pouvoir réessayer après verrouillage</p>
              </div>
              <input
                id="lockoutDurationMinutes"
                type="number"
                min="5"
                max="60"
                step="5"
                value={settings.lockoutDurationMinutes}
                onChange={(e) => handleChange('lockoutDurationMinutes', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="emailLinkExpiryHours" className="block text-sm font-medium text-slate-700">
                  Expiration liens email (heures)
                </label>
                <p className="text-xs text-slate-500">Durée de validité des liens de vérification et changement d'email</p>
              </div>
              <input
                id="emailLinkExpiryHours"
                type="number"
                min="1"
                max="168"
                value={settings.emailLinkExpiryHours}
                onChange={(e) => handleChange('emailLinkExpiryHours', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label htmlFor="accountDeletionDelayDays" className="block text-sm font-medium text-slate-700">
                  Délai de suppression de compte (jours)
                </label>
                <p className="text-xs text-slate-500">Délai de grâce avant suppression définitive (RGPD)</p>
              </div>
              <input
                id="accountDeletionDelayDays"
                type="number"
                min="1"
                max="90"
                value={settings.accountDeletionDelayDays}
                onChange={(e) => handleChange('accountDeletionDelayDays', parseInt(e.target.value, 10))}
                className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* SMTP Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <span>📧</span>
            Configuration Email (SMTP)
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Configurez le serveur SMTP pour l'envoi des emails (codes 2FA, réinitialisation mot de passe, etc.)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="smtpHost" className="block text-sm font-medium text-slate-700 mb-1">
                Serveur SMTP
              </label>
              <input
                id="smtpHost"
                type="text"
                value={settings.smtpHost}
                onChange={(e) => handleChange('smtpHost', e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="smtpPort" className="block text-sm font-medium text-slate-700 mb-1">
                Port
              </label>
              <input
                id="smtpPort"
                type="number"
                value={settings.smtpPort}
                onChange={(e) => handleChange('smtpPort', parseInt(e.target.value, 10))}
                placeholder="587"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="smtpUser" className="block text-sm font-medium text-slate-700 mb-1">
                Utilisateur
              </label>
              <input
                id="smtpUser"
                type="text"
                value={settings.smtpUser}
                onChange={(e) => handleChange('smtpUser', e.target.value)}
                placeholder="votre@email.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="smtpPass" className="block text-sm font-medium text-slate-700 mb-1">
                Mot de passe
              </label>
              <input
                id="smtpPass"
                type="password"
                value={settings.smtpPass}
                onChange={(e) => handleChange('smtpPass', e.target.value)}
                placeholder={settings.smtpPass === '********' ? '(inchangé)' : 'Mot de passe SMTP'}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="smtpFromName" className="block text-sm font-medium text-slate-700 mb-1">
                Nom expéditeur
              </label>
              <input
                id="smtpFromName"
                type="text"
                value={settings.smtpFromName}
                onChange={(e) => handleChange('smtpFromName', e.target.value)}
                placeholder="Spellion"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">Nom affiché comme expéditeur des emails (ex: "Spellion")</p>
            </div>

            <div>
              <label htmlFor="smtpFrom" className="block text-sm font-medium text-slate-700 mb-1">
                Email expéditeur
              </label>
              <input
                id="smtpFrom"
                type="email"
                value={settings.smtpFrom}
                onChange={(e) => handleChange('smtpFrom', e.target.value)}
                placeholder="noreply@spellion.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="adminEmail" className="block text-sm font-medium text-slate-700 mb-1">
                Email admin (contact)
              </label>
              <input
                id="adminEmail"
                type="email"
                value={settings.adminEmail}
                onChange={(e) => handleChange('adminEmail', e.target.value)}
                placeholder="admin@spellion.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">Adresse qui recevra les messages du formulaire de contact</p>
            </div>

            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.smtpSecure}
                  onChange={(e) => handleChange('smtpSecure', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">Connexion sécurisée (SSL/TLS)</span>
              </label>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleTestSmtp}
                disabled={isTesting || !settings.smtpHost || !settings.smtpUser}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTesting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Test en cours...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Tester la connexion</span>
                  </>
                )}
              </button>
            </div>

            {/* SMTP Test Result Message */}
            {smtpTestMessage && (
              <div
                className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
                  smtpTestMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                {smtpTestMessage.type === 'success' ? (
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className="text-sm">{smtpTestMessage.text}</span>
              </div>
            )}
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <strong>Gmail :</strong> Utilisez un "mot de passe d'application" (Paramètres Google &gt; Sécurité &gt; Mots de passe des applications)
              <br />
              <strong>Outlook :</strong> smtp.office365.com, port 587
              <br />
              <strong>SendGrid :</strong> smtp.sendgrid.net, port 587
            </p>
          </div>
        </div>

        {/* Save button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Enregistrer les paramètres</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
